import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import EmailInboxManagementDialog from './EmailInboxManagementDialog';
import EmailInboxList from './EmailInboxList';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { gray40, gray100, primaryButtonBackgroundColor, white } from '../../../constants/colors';
import { boldXxl, spHeaderTitle } from '../../../constants/location';

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  headerButton: {
    height: '44px',
    padding: '0 16px',
    whiteSpace: 'nowrap' as const,
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  section: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    margin: '0 32px',
    minHeight: '560px',
    padding: '8px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
};

/**
 * メール取り込みの受信アドレス管理画面（#3042）。専用アドレスの一覧表示・管理を行う。
 * 発行済みアドレスの確認・有効/無効・削除を行う。発行は発行ダイアログから。
 */
const EmailInboxListPage: React.FC = () => {
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  const [issueOpen, setIssueOpen] = useState(false);
  // 発行後に一覧を再読み込みするためのキー
  const [reloadKey, setReloadKey] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarInfoText, setSnackbarInfoText] = useState('');
  const [snackbarErrorText, setSnackbarErrorText] = useState('');
  const handleNotify = (message: string, isError = false): void => {
    setSnackbarInfoText(isError ? '' : message);
    setSnackbarErrorText(isError ? message : '');
    setSnackbarOpen(true);
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="受信アドレス管理" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box
          sx={[
            layoutStyles.header,
            { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
          ]}
        >
          <Typography sx={styles.headerTitle}>受信アドレス管理</Typography>
          <Button
            onClick={(): void => setIssueOpen(true)}
            startIcon={<AddIcon sx={{ height: '20px', width: '20px' }} />}
            sx={[primaryButtonBackgroundColor, styles.headerButton]}
          >
            アドレス発行
          </Button>
        </Box>

        <Box sx={styles.section}>
          <EmailInboxList onNotify={handleNotify} reloadKey={reloadKey} />
        </Box>
      </Box>

      <EmailInboxManagementDialog
        open={issueOpen}
        onClose={(): void => setIssueOpen(false)}
        onNotify={handleNotify}
        onIssued={(): void => {
          setIssueOpen(false);
          setReloadKey(prev => prev + 1);
        }}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarErrorText}
        infoText={snackbarInfoText}
      />
    </Box>
  );
};

export default EmailInboxListPage;
